import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';
import { api, ApiError } from './api';
import type { Admin } from './types';

type AuthContextValue = {
  admin: Admin | null;
  loading: boolean;
  login: (email: string, password: string, mfaCode?: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshAdmin: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshAdmin = useCallback(async () => {
    const result = await api<{ admin: Admin }>('/auth/me');
    setAdmin(result.admin);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const retryDelaysMs = [600, 1200, 2400];

    async function checkSession() {
      for (let attempt = 0; ; attempt++) {
        try {
          const result = await api<{ admin: Admin }>('/auth/me');
          if (!cancelled) setAdmin(result.admin);
          return;
        } catch (error) {
          const isExpiredSession =
            error instanceof ApiError && error.status === 401;
          if (isExpiredSession || attempt >= retryDelaysMs.length) {
            if (!isExpiredSession) console.error(error);
            return;
          }
          await new Promise((resolve) =>
            setTimeout(resolve, retryDelaysMs[attempt]),
          );
        }
      }
    }

    checkSession().finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const handleSessionExpired = () => setAdmin(null);
    window.addEventListener('qui:session-expired', handleSessionExpired);
    return () => window.removeEventListener('qui:session-expired', handleSessionExpired);
  }, []);

  const login = useCallback(
    async (email: string, password: string, mfaCode?: string) => {
      const result = await api<{ admin: Admin }>('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password, ...(mfaCode ? { mfaCode } : {}) }),
      });
      setAdmin(result.admin);
    },
    [],
  );

  const logout = useCallback(async () => {
    try {
      await api('/auth/logout', { method: 'POST' });
    } finally {
      setAdmin(null);
    }
  }, []);

  const value = useMemo(
    () => ({ admin, loading, login, logout, refreshAdmin }),
    [admin, loading, login, logout, refreshAdmin],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('AuthProvider missing');
  return value;
}
